import { Injectable } from '@angular/core';
import { PostPayloadInterface } from '../../models/post-payload.interface';
import { TranslationRepository } from '../../repositories/translation.repository';
import { NotificationService } from '../common/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { Utilities } from '../../utils/utilities';
import { CoreDataService } from "./core-data.service";
import { DownloadUrlInfo } from "../../directives/download-file.directive";
import { FilterService } from "../filterer/filter.service";
import { Dtos } from "../../utils/dtos";

export class TranslationLocalization {
  public value: string

  constructor(value?: string) {
    this.value = value;
  }
}

export class TranslationModel {
  public id = null;
  public environment;
  public translationKey: string;
  public subTranslations: TranslationModel[] = [];
  public localizations: any;

  constructor(environment: string, translationObject?: any) {
    this.environment = environment;
    if (translationObject != undefined) {
      this.id = translationObject.id;
      this.translationKey = translationObject.translationKey;
      this.localizations = translationObject.localizations;
      translationObject.subTranslations.forEach(subTrans => {
        this.subTranslations.push(new TranslationModel(subTrans.environment, subTrans));
      })
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class TranslationService {

  public translations: any[] = [];
  public allTranslations: any[] = [];
  public authUser: any;
  public languages: any[];
  public isOpen: boolean[] = [];
  public allEdit: boolean;
  public environment: string;
  private translationsToUpdate: any = {};

  constructor(private translationRepository: TranslationRepository,
              private notificationService: NotificationService,
              private window: Window,
              public coreDataService: CoreDataService,
              private filterService: FilterService,
              private translateService: TranslateService) {
    this.languages = coreDataService.getLanguages();
  }

  addNewModel() {
    this.translations.unshift(new TranslationModel(this.environment));
  }

  loadData() {
    this.translations = [];
    this.isOpen = [];
    this.allTranslations = [];
    this.translationRepository.getAllByEnvironment(this.environment).subscribe((data: any[]) => {
      data.forEach((translation) => {
        this.translations.push(new TranslationModel(translation.environment, translation));
        this.allTranslations.push(new TranslationModel(translation.environment, translation));
        this.isOpen.push(false);
      });
    });
  }

  getTranslationsByEnvironment(env) {
    return this.translationRepository.getAllByEnvironment(env);
  }

  getTranslationsByFilterer(env, rootKey: string) {
    const filter = this.filterService.filter(Dtos.FRONTEND_TRANSLATION.FrontendTranslationDTO)
      .equal("environment", env)
      .and()
      .equal("translationKey", rootKey)
      .and()
      .isNull("parent")
      .create();
    return this.translationRepository.getAllByFilter(filter);
  }

  save() {
    let payload: PostPayloadInterface;
    if (!this.allEdit) {
      Object.values<any>(this.translationsToUpdate).forEach(translation => {
        payload = {
          id: translation.id,
          type: 'Translate',
          attribute: translation
        };
        this.translationRepository.updateWithoutId(payload).subscribe(data => {
          this.notificationService.showSuccessWithoutTranslation(this.translateService.instant('general.success') + ': ' + translation.translationKey);
          setTimeout(() => {
            this.window.location.reload();
          }, 2000)

        });
      });
    } else {
      payload = {
        id: 0,
        type: 'Translate',
        attribute: this.translations
      };
      this.translationRepository.save(payload).subscribe(data => {
        this.notificationService.showSuccess('general.success');
        this.window.location.reload();
      });
    }

  }

  update(translation) {
    let payload: PostPayloadInterface = {
      id: translation.id,
      type: 'Translate',
      attribute: translation
    };
    return this.translationRepository.updateWithoutId(payload)
  }

  deleteTranslation(translation) {
    if (confirm(this.translateService.instant('translation.deleteConfirm'))) {
      if (translation.id > 0) {
        this.translationRepository.deleteById(translation.id).subscribe(response => {
          this.removeTranslationElementFromList(this.translations, translation);
        });
      } else {
        this.removeTranslationElementFromList(this.translations, translation)
      }
    }
  }

  private removeTranslationElementFromList(list, translation, isRoot = true) {
    const index = list.indexOf(translation, 0);
    if (index > -1) {
      list.splice(index, 1);
      if (isRoot) {
        this.isOpen.splice(index, 1);
      }
      return;
    }

    for (let listElement of list) {
      if (listElement.subTranslations != undefined && listElement.subTranslations != null) {
        for (let sublistElement of listElement.subTranslations) {
          this.removeTranslationElementFromList(listElement.subTranslations, translation, false);
        }
      }
    }
  }

  exportTranslations() {
    this.window.location.href = Utilities.getTranslateUrl() + this.environment;
  }

  getExportAsInfo(exportType: string): DownloadUrlInfo {
    return {
      url: Utilities.getTranslateUrl() + this.environment + '/export/' + exportType,
      fileName: 'translations_' + this.environment + '_' + Utilities.dateToBackendString(new Date()) + '.' + exportType
    };
  }

  clearFilters() {
    this.translations = [];
    this.isOpen = [];
    this.allTranslations.forEach(trans => {
      this.translations.push(new TranslationModel(trans.environment, trans));
      this.isOpen.push(false);
    });
  }

  filterByKey(searchByKey: string) {
    let keyElements = searchByKey.split('.');
    this.translations = [];
    this.allTranslations.forEach(trans => {
      if (trans.translationKey == keyElements[0]) {
        this.translations.push(new TranslationModel(trans.environment, trans));
      }
    });
    this.isOpen = [];
    this.translations.forEach(translation => {
      translation.subTranslations = this.subTranslationsKeySearch(translation.subTranslations, keyElements, 1);
      this.isOpen.push(true);
    });
  }

  filterByKeyOnArray(searchByKey: string, translations: TranslationModel[]) {
     let keyElements = searchByKey.split('.');
    let filteredTranslations = translations;
    for (let i = 0; i < keyElements.length; i++) {
      let trs = filteredTranslations.filter(trans => {
        return trans.translationKey == keyElements[i];
      })
      if (trs.length > 0 && i != (keyElements.length - 1)) {
        filteredTranslations = trs[0].subTranslations;
      }
      if (trs.length > 0 && i == (keyElements.length - 1)) {
        filteredTranslations = trs;
      }
    }
    if (filteredTranslations.length > 1) {
      return null;
    }
    return filteredTranslations[0];
  }

  public getTranslationByKey(key: string, translations: any[], langCode: string): string {
    const subTranslation = this.filterByKeyOnArray(key, translations);
    if (!subTranslation) {
      return key;
    }
    return subTranslation.localizations?.[langCode]?.value ? subTranslation.localizations[langCode].value : key;
  }

  subTranslationsKeySearch(subTranslations: any[], keyElements: any[], stepp: number) {

    if (subTranslations.length > 0 && keyElements.length > stepp) {
      let tmp = [];
      subTranslations.forEach(subTranslation => {
        if (subTranslation.translationKey == keyElements[stepp]) {
          subTranslation.subTranslations = this.subTranslationsKeySearch(subTranslation.subTranslations, keyElements, ++stepp);
          tmp.push(Object.assign({}, subTranslation));
        }
      });
      return tmp;
    }
    return subTranslations;
  }

  filterByContent(searchByContent: string) {
    this.translations = [];
    this.isOpen = [];
    let allTrans = [];
    let findedKeys = [];
    this.allTranslations.forEach(trans => {
      allTrans.push(new TranslationModel(trans.environment, trans));
    });
    allTrans.forEach(translation => {
      let findedKey = this.localizationSearch(translation, searchByContent);
      if (findedKey != null) {
        findedKeys.push(translation.translationKey + '.' + findedKey);
      }
    });
    findedKeys.forEach(key => {
      let keyElements = key.split('.');
      this.allTranslations.forEach(trans => {
        if (trans.translationKey == keyElements[0]) {
          let newTranslation = new TranslationModel(trans.environment, trans);
          newTranslation.subTranslations = this.subTranslationsKeySearch(newTranslation.subTranslations, keyElements, 1);
          this.translations.push(newTranslation);
          this.isOpen.push(true);
        }
      });
    });
  }

  private localizationSearch(translation: any, searchByContent: string) {
    let result = null;
    if (translation.subTranslations.length > 0) {
      translation.subTranslations.forEach(subTrans => {
        let key = this.localizationSearch(subTrans, searchByContent);
        if (key != null) {
          result = subTrans.translationKey + '.' + key;
        }
      });
    } else {
      Object.keys(translation.localizations).forEach(langKey => {
        if (translation.localizations[langKey].value == searchByContent) {
          result = translation.translationKey;
        }
      });
    }
    return result;
  }

  addToUpdate(translation) {
    this.translationsToUpdate[translation.id] = translation;
  }

  importCsvByEnvironment(file: File) {
    let environment = file.name.split('_', 2)[1];

    if (environment == undefined) {
      this.notificationService.showError('translation.import.fileMismatch')
    } else {
      this.translationRepository.importCsvByEnvironment(environment, file).subscribe(resp => {
        this.notificationService.showSuccess("general.success");
        setTimeout(location.reload.bind(location), 1500);
      });
    }
  }

}
